import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Checkbox, Descriptions, Input, InputNumber, Modal, Popover, Select, Switch } from 'antd';
import { DescriptionsItemProps } from 'antd/es/descriptions/Item';
import Table, { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import * as paymentApi from '@apis/paymentStatements';
import * as salesChannelApi from '@apis/sales-channels';
import { useAuthentication } from '@hooks/authentication';
import { PaymentStatementPurchase, RefundBody } from '@models/paymentStatement';
import { SalesChannelsType } from '@models/sales-channels';
import { PARTNER_PURCHASES_QUERY_KEY } from '@pages/SalesHistoryPage';
import { EZWEL_PARTNER_REFUND_GUIDE } from '@utils/common';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import useRefundModalStore from '@stores/useRefundModalStore';
import { NAVER_PAYED_PARTNER_REFUND_BLOCKED_MESSAGE } from '@utils/payment';
import './RefundProductOrderModal.scss';

const isNaverPayedRefundBlocked = (channel?: SalesChannelsType, naverProductOrderStatus?: string) =>
  channel === SalesChannelsType.NAVER_STORE && naverProductOrderStatus === 'PAYED';

const ADMIN_MEMO_MARKER = '[관리자]';

const REFUND_REASON_OPTIONS = [
  { key: 'repay', label: '재결제', aliases: ['재결제 진행'] },
  { key: 'anotherProduct', label: '다른 상품을 구매' },
  { key: 'cheaper', label: '더 저렴한 상품을 찾음' },
  { key: 'schedule', label: '스케줄 및 일정 문제' },
  { key: 'accident', label: '아이질병 및 사고 등 건강문제' },
  { key: 'changeMind', label: '단순 변심' },
  { key: 'shortage', label: '수업 인원 부족' },
  { key: 'externalPlatform', label: '외부 플랫폼 구매' },
  { key: 'etc', label: '기타 (내용 작성)', aliases: ['기타'] },
];

const findRefundReasonOption = (label?: string) => {
  const normalized = label?.trim();
  if (!normalized) {
    return undefined;
  }
  return REFUND_REASON_OPTIONS.find(
    (option) =>
      option.label === normalized ||
      option.aliases?.includes(normalized) ||
      (option.key === 'etc' && normalized.startsWith('기타')),
  );
};

const parseRefundReasonValue = (value?: string) => {
  if (!value || value === '-') {
    return { reasonKey: '', customerReasonText: '', memoText: '' };
  }
  const slashIndex = value.indexOf('/');
  if (slashIndex === -1) {
    const matchedOption = findRefundReasonOption(value);
    return { reasonKey: matchedOption?.key || '', customerReasonText: '', memoText: '' };
  }
  const reasonLabel = value.slice(0, slashIndex);
  const detail = value.slice(slashIndex + 1);
  const matchedOption = findRefundReasonOption(reasonLabel);
  if (matchedOption?.key === 'etc') {
    const adminMemoIndex = detail.search(/\n\[관리자\]/);
    if (adminMemoIndex >= 0) {
      return {
        reasonKey: matchedOption.key,
        customerReasonText: detail.slice(0, adminMemoIndex).trim(),
        memoText: detail
          .slice(adminMemoIndex)
          .replace(/^\n\[관리자\]\n?/, '')
          .trim(),
      };
    }
    return { reasonKey: matchedOption.key, customerReasonText: detail.trim(), memoText: '' };
  }
  return { reasonKey: matchedOption?.key || '', customerReasonText: '', memoText: detail };
};

const buildRefundPartnerMemo = (reasonKey: string, memoText: string, customerReasonText = '') => {
  const selectedOption = REFUND_REASON_OPTIONS.find((option) => option.key === reasonKey);
  if (!selectedOption) {
    return '';
  }
  if (reasonKey === 'etc') {
    const customerReason = customerReasonText.trim();
    const adminMemo = memoText.trim();
    if (adminMemo) {
      return `${selectedOption.label}/${customerReason}\n${ADMIN_MEMO_MARKER}\n${adminMemo}`;
    }
    return `${selectedOption.label}/${customerReason}`;
  }
  return `${selectedOption.label}/${memoText}`;
};

const NotRefundSwitch: React.FC<{ checked: boolean; onChange: () => void }> = (props) => {
  return (
    <Popover
      content={
        <div>
          구매자에게 해당 결제 건에 대해 앞으로 환불 요청 버튼이 노출이 되지 않습니다.
          <br />
          구매자와 충분히 상의하시고 체크해주세요.
        </div>
      }>
      <Switch checked={props.checked} onChange={props.onChange} />
    </Popover>
  );
};

const RefundProductOrderModal: React.FC = () => {
  const { authStore } = useAuthentication();
  const { data, step, show, close, changeStep } = useRefundModalStore();
  const dialog = useOkCancelDialog();
  const queryClient = useQueryClient();
  const [originRefundTargets, setOriginRefundTargets] = useState<PaymentStatementPurchase[]>([]);
  const [refundTargets, setRefundTargets] = useState<PaymentStatementPurchase[]>([]);
  const [refundAmount, setRefundAmount] = useState(0);
  const [noMoreRefundsAvailable, setNoMoreRefundsAvailable] = useState(false);
  const [selectedRefundReasonKey, setSelectedRefundReasonKey] = useState('');
  const [customerReasonText, setCustomerReasonText] = useState('');
  const memoRef = useRef<HTMLTextAreaElement>(null);

  const refundPaymentStatementMutation = useMutation({
    mutationFn: paymentApi.submitRefundDecision,
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_PURCHASES_QUERY_KEY(authStore.user)] });
      queryClient.invalidateQueries({ queryKey: [`product-orders-${authStore?.user?.id}`] });
      close();
      const isDeny = refundPaymentStatementMutation?.variables?.body?.type === 'deny';
      dialog.open({
        content: isDeny ? '환불 반려요청이 완료되었습니다.' : '환불예정 처리가 완료되었습니다.',
        type: 'ok',
      });
    },
    onError: async (e: any) => {
      const isDeny = refundPaymentStatementMutation?.variables?.body?.type === 'deny';
      dialog.open({
        title: isDeny ? '환불 반려요청 실패' : '환불예정 처리 실패',
        content: `${e?.response?.data?.message}`,
        type: 'ok',
      });
    },
  });
  const refundSalesChannelOrderMutation = useMutation({
    mutationFn: salesChannelApi.handleRefund,
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_PURCHASES_QUERY_KEY(authStore.user)] });
      queryClient.invalidateQueries({ queryKey: [`product-orders-${authStore?.user?.id}`] });
      close();
      const isDeny = refundSalesChannelOrderMutation?.variables?.body?.type === 'deny';
      dialog.open({
        content: isDeny ? '환불 반려요청이 완료되었습니다.' : '환불예정 처리가 완료되었습니다.',
        type: 'ok',
      });
    },
    onError: async (e: any) => {
      dialog.open({
        title: '환불 요청 실패',
        content: `${e?.response?.data?.message}`,
        type: 'ok',
      });
    },
  });

  const handleAcceptRefund = () => {
    if (data?.channel === SalesChannelsType.EZWEL) {
      dialog.open({
        title: EZWEL_PARTNER_REFUND_GUIDE.title,
        content: EZWEL_PARTNER_REFUND_GUIDE.content,
        type: 'ok',
      });
      return;
    }
    if (isNaverPayedRefundBlocked(data?.channel, data?.naverProductOrderStatus)) {
      dialog.open({
        title: '환불 처리 불가',
        content: NAVER_PAYED_PARTNER_REFUND_BLOCKED_MESSAGE,
        type: 'ok',
      });
      return;
    }
    const refundTargetAmount = data?.channel ? data?.remainAmount || 0 : refundAmount;
    if (refundTargetAmount < 1) {
      dialog.open({
        content: '환불 금액을 확인해주세요.',
        type: 'ok',
      });
      return;
    }

    const memoText = memoRef.current?.value?.trim() || '';
    if (!selectedRefundReasonKey) {
      dialog.open({
        title: '필수',
        content: '환불 사유를 선택해주세요.',
        type: 'ok',
      });
      return;
    }
    if (selectedRefundReasonKey === 'etc' && !customerReasonText.trim()) {
      dialog.open({
        title: '필수',
        content: '기타 사유를 선택한 경우 고객 안내 사유를 입력해주세요.',
        type: 'ok',
      });
      return;
    }

    dialog.open({
      title: '환불요청을 승인하시겠어요?',
      content:
        '환불예정으로 변경되면 철회가 불가능하며, 파트너가 선택한 환불사유가 고객에게 알림톡으로 전달됩니다. 사전에 고객과 환불사유가 협의되지 않았을 때 발생하는 고객 클레임은 파트너의 귀책이 될 수 있습니다.',
      type: 'ok_cancel',
      onConfirm: () => {
        if (data) {
          const updateBody: RefundBody = {
            type: 'accept',
            fee: refundAmount,
            noMoreRefundsAvailable,
            content: buildRefundPartnerMemo(selectedRefundReasonKey, memoText, customerReasonText),
            refundItems: refundTargets.map((refundTaget) => ({
              ...refundTaget,
              fee: Math.floor((refundTaget.fee || 0) / (refundTaget.count || 1)),
            })),
          };
          if (data?.channel) {
            refundSalesChannelOrderMutation.mutate({
              orderId: data.orderId,
              body: updateBody,
            });
          } else {
            refundPaymentStatementMutation.mutate({
              paymentStatementId: data.orderId,
              body: updateBody,
            });
          }
        }
      },
    });
  };
  const handleDenyRefund = () => {
    if (data?.channel === SalesChannelsType.EZWEL) {
      dialog.open({
        title: EZWEL_PARTNER_REFUND_GUIDE.title,
        content: EZWEL_PARTNER_REFUND_GUIDE.content,
        type: 'ok',
      });
      return;
    }
    if (!memoRef.current?.value?.trim()) {
      dialog.open({
        content: '환불 반려 사유를 입력해주세요.',
        type: 'ok',
      });
      return;
    } else {
      dialog.open({
        title: '환불을 반려하시겠어요?',
        content: '고객이 신청한 환불신청정보가 취소됩니다\n확인 버튼을 누르시면 고객에게 알림이 발송됩니다.',
        type: 'ok_cancel',
        onConfirm: () => {
          if (data && memoRef.current) {
            if (data?.channel) {
              refundSalesChannelOrderMutation.mutate({
                orderId: data.orderId,
                body: {
                  type: 'deny',
                  content: memoRef.current.value,
                } as RefundBody,
              });
            } else {
              refundPaymentStatementMutation.mutate({
                paymentStatementId: data.orderId,
                body: {
                  type: 'deny',
                  noMoreRefundsAvailable,
                  content: memoRef.current.value,
                  refundItems: data.refundInfo?.purchases || [],
                },
              });
            }
          }
        },
      });
    }
  };
  const handleRequestRefund = () => {
    if (data?.channel === SalesChannelsType.EZWEL) {
      dialog.open({
        title: EZWEL_PARTNER_REFUND_GUIDE.title,
        content: EZWEL_PARTNER_REFUND_GUIDE.content,
        type: 'ok',
      });
      return;
    }
    if (isNaverPayedRefundBlocked(data?.channel, data?.naverProductOrderStatus)) {
      dialog.open({
        title: '환불 요청 불가',
        content: NAVER_PAYED_PARTNER_REFUND_BLOCKED_MESSAGE,
        type: 'ok',
      });
      return;
    }
    const memoText = memoRef.current?.value?.trim() || '';

    if (!selectedRefundReasonKey || (!data?.channel && !refundTargets?.length)) {
      dialog.open({
        title: '필수',
        content: '환불 사유와 환불 대상을 확인해주세요.',
        type: 'ok',
      });
      return;
    }
    if (selectedRefundReasonKey === 'etc' && !customerReasonText.trim()) {
      dialog.open({
        title: '필수',
        content: '기타 사유를 선택한 경우 고객 안내 사유를 입력해주세요.',
        type: 'ok',
      });
      return;
    }
    dialog.open({
      title: '환불을 요청하시겠어요?',
      content:
        '확인 버튼을 누르시면 ‘환불예정’ 상태로 전환되며 철회가 불가능합니다. 파트너가 선택한 환불사유가 고객에게 알림톡으로 전달되며, 사전에 고객과 환불사유가 협의되지 않았을 때 발생하는 고객 클레임은 파트너의 귀책이 될 수 있습니다.',
      type: 'ok_cancel',
      onConfirm: () => {
        if (data) {
          const updateBody: RefundBody = {
            type: 'request',
            fee: refundAmount,
            noMoreRefundsAvailable,
            content: buildRefundPartnerMemo(selectedRefundReasonKey, memoText, customerReasonText),
            refundItems: refundTargets.map((item) => {
              return { ...item, fee: Math.floor((item.fee || 0) / (item.count || 1)) };
            }),
          };

          if (data?.channel) {
            updateBody['channel'] = data?.channel;
            refundSalesChannelOrderMutation.mutate({
              orderId: data.orderId,
              body: updateBody,
            });
          } else {
            refundPaymentStatementMutation.mutate({
              paymentStatementId: data.orderId,
              body: updateBody,
            });
          }
        }
      },
    });
  };
  const getMatchedItem = (searchItems?: PaymentStatementPurchase[], target?: PaymentStatementPurchase) => {
    if (searchItems && target) {
      const index = searchItems.findIndex((searchItem) => {
        if (target?.uid) {
          return searchItem?.uid === target?.uid;
        } else {
          return JSON.stringify(searchItem.name) === JSON.stringify(target?.name);
        }
      });
      return searchItems[index];
    }
  };
  const getRefundItemIndex = (target: PaymentStatementPurchase) => {
    return refundTargets.findIndex((refundTarget) => {
      if (refundTarget?.uid) {
        return refundTarget?.uid === target?.uid;
      } else {
        return JSON.stringify(refundTarget.name) === JSON.stringify(target.name);
      }
    });
  };

  useEffect(() => {
    setOriginRefundTargets([]);
    setRefundTargets([]);
    if (memoRef.current) {
      memoRef.current.value = '';
    }
    setRefundAmount(0);
    setNoMoreRefundsAvailable(false);
    setSelectedRefundReasonKey('');
    setCustomerReasonText('');
  }, [data, show]);

  useEffect(() => {
    if (!show || data?.channel !== SalesChannelsType.EZWEL) {
      return;
    }
    close();
    dialog.open({
      title: EZWEL_PARTNER_REFUND_GUIDE.title,
      content: EZWEL_PARTNER_REFUND_GUIDE.content,
      type: 'ok',
    });
  }, [show, data, close, dialog]);

  useEffect(() => {
    if (!show || step !== 'ACCEPTED') {
      return;
    }
    const { reasonKey, customerReasonText: parsedCustomerReasonText } = parseRefundReasonValue(data?.reason);
    setSelectedRefundReasonKey(reasonKey);
    setCustomerReasonText(parsedCustomerReasonText);
  }, [data?.reason, show, step]);

  useEffect(() => {
    if (!show || step !== 'PARTNER_REQUEST') {
      return;
    }
    const { reasonKey, customerReasonText: parsedCustomerReasonText, memoText } = parseRefundReasonValue(
      data?.memo?.refundPartner,
    );
    setSelectedRefundReasonKey(reasonKey);
    setCustomerReasonText(parsedCustomerReasonText);
    if (memoRef.current) {
      memoRef.current.value = memoText;
    }
  }, [data?.memo?.refundPartner, show, step]);

  useEffect(() => {
    const refundItems = data?.refundInfo?.refundItems || data?.refundInfo?.purchases || [];

    if (data?.remainPurchases && data?.remainPurchases.length > 0 && data?.type === 'partner_request') {
      changeStep('PARTNER_REQUEST');
    } else {
      setOriginRefundTargets(refundItems);
      refundItems.forEach((item) => {
        setRefundTargets((prev) => {
          const newValue = [...prev];
          newValue.push({ ...item, fee: item.count * (item.fee || 0) });
          return newValue;
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, show]);

  useEffect(() => {
    const refundAmount = refundTargets.reduce((prev, cur) => prev + (cur.fee || 0), 0);
    setRefundAmount(refundAmount);
  }, [refundTargets]);

  const REFUND_INFO: DescriptionsItemProps[] = [
    { span: 3, label: '상품명', children: data?.productName },
    { span: 3, label: '환불 접수일시', children: moment(data?.requestAt).format('YYYY.MM.DD. HH:mm') },
    {
      span: 3,
      label: '환불 가능 금액',
      children: data?.channel
        ? `${data?.remainAmount?.toLocaleString()}원`
        : `${data?.refundInfo?.requestedAmount?.toLocaleString()}원`,
    },
    { span: 3, label: '환불 사유', children: data?.reason },
  ];
  const ALLOW_TABLE_COLUMNS: ColumnsType<PaymentStatementPurchase> = [
    {
      title: '상품명',
      width: 230,
      render: (item) => {
        let option = '';
        if (item.option && item.option.length > 0) {
          option = `[${item.option.join(' ')}]`;
        }
        return option + item.name;
      },
      align: 'center',
    },
    {
      title: '개당\n가격',
      width: 60,
      render: (item) => `${(item.fee || 0).toLocaleString()}`,
      align: 'center',
    },
    {
      title: '구매\n수량',
      width: 60,
      render: (item) => item.count || 0,
      align: 'center',
    },
    {
      title: '남은\n수량',
      width: 60,
      render: (item) => item.count - (getMatchedItem(data?.historyRefundItems, item)?.count || 0),
      align: 'center',
    },
    {
      title: (
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', top: '-5px', right: '10px', color: 'red' }}>*</div>
          <div>{`일정 취소\n수량`}</div>
        </div>
      ),
      width: 60,
      render: (item) => {
        const refundTarget = getMatchedItem(refundTargets, item);
        const refundItemIndex = getRefundItemIndex(item);

        return (
          <InputNumber
            value={refundTarget?.count || 0}
            min={0}
            max={originRefundTargets[refundItemIndex]?.count || 0}
            onChange={(value) => {
              setRefundTargets((prev) => {
                const newValue = [...prev];
                newValue[refundItemIndex].count = value || 0;
                newValue[refundItemIndex].fee = (value || 0) * originRefundTargets[refundItemIndex].fee;

                return newValue;
              });
            }}
            disabled={!refundTarget}
          />
        );
      },
      align: 'center',
    },
    {
      title: (
        <div style={{ position: 'relative' }}>
          <div>
            {`환불 금액`}
            <span style={{ color: 'red' }}>**</span>
          </div>
        </div>
      ),
      width: 180,
      render: (item) => {
        const refundTarget = getMatchedItem(refundTargets, item);
        const refundItemIndex = getRefundItemIndex(item);

        return (
          <InputNumber
            value={refundTarget?.fee || 0}
            min={0}
            max={(originRefundTargets[refundItemIndex]?.fee || 0) * (originRefundTargets[refundItemIndex]?.count || 0)}
            onChange={(value) => {
              setRefundTargets((prev) => {
                const newValue = [...prev];
                newValue[refundItemIndex].fee = value || 0;

                return newValue;
              });
            }}
            disabled={!refundTarget}
            addonAfter={
              !!refundTarget && (
                <Select
                  defaultValue={100}
                  popupMatchSelectWidth={false}
                  suffixIcon={null}
                  options={Array.from({ length: 20 }, (_, idx) => ({
                    value: 5 * (idx + 1),
                    label: `${5 * (idx + 1)}%`,
                  }))}
                  onChange={(e) => {
                    setRefundTargets((prev) => {
                      const newValue = [...prev];
                      newValue[refundItemIndex].fee = Math.floor(
                        (originRefundTargets[refundItemIndex]?.fee || 0) *
                          (originRefundTargets[refundItemIndex]?.count || 0) *
                          (e / 100),
                      );
                      return newValue;
                    });
                  }}
                />
              )
            }
          />
        );
      },
      align: 'center',
    },
  ];
  const REQUEST_REFFUND_TABLE_COLUMNS: ColumnsType<PaymentStatementPurchase> = [
    {
      title: '선택',
      width: 60,
      render: (item) => {
        return (
          <Checkbox
            className="black-checkbox"
            disabled={item.fee === 0}
            checked={getRefundItemIndex(item) > -1}
            onChange={() => {
              setOriginRefundTargets((prev) => {
                const newValue = [...prev];
                newValue.push(item);
                return newValue;
              });
              setRefundTargets((prev) => {
                const newValue = [...prev];
                const found = getRefundItemIndex(item) > -1;
                if (found) {
                  newValue.splice(getRefundItemIndex(item), 1);
                } else {
                  newValue.push({ ...item, fee: item.count * (item.fee || 0) });
                }

                return newValue;
              });
            }}
          />
        );
      },
      align: 'center',
      hidden: !!data?.channel,
    },
    {
      title: '상품명',
      render: (item) => {
        let option = '';
        if (item.option && item.option.length > 0) {
          option = `[${item.option.join(' ')}]`;
        }
        return option + item.name;
      },
      align: 'center',
    },
    {
      title: '개당\n가격(잔액)',
      width: 100,
      render: (item) => {
        let fee = item?.fee || 0;
        if (data?.channel === SalesChannelsType.NAVER_STORE) {
          fee = (item?.fee || data?.remainAmount || 0) / item?.count || 1;
        }
        return `${Math.floor(fee).toLocaleString()}`;
      },
      align: 'center',
    },
    {
      title: '구매\n수량',
      width: 80,
      render: (item) => item.count || 0,
      align: 'center',
    },
    {
      title: '남은\n수량',
      width: 80,
      render: (item) => item.count || 0,
      align: 'center',
    },
    {
      title: (
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', top: '-5px', right: '10px', color: 'red' }}>*</div>
          <div>{`일정 취소\n수량`}</div>
        </div>
      ),
      width: 60,
      render: (item) => {
        const refundItemIndex = getRefundItemIndex(item);
        let target = undefined;
        if (data?.channel) {
          target = getMatchedItem([...(data?.remainPurchases || [])], item);
        } else {
          target = getMatchedItem([...refundTargets], item);
        }
        return (
          <InputNumber
            value={target?.count || 0}
            min={0}
            max={originRefundTargets[refundItemIndex]?.count || 0}
            onChange={(value) => {
              setRefundTargets((prev) => {
                const newValue = [...prev];
                newValue[refundItemIndex].count = value || 0;
                newValue[refundItemIndex].fee = (value || 0) * originRefundTargets[refundItemIndex].fee;

                return newValue;
              });
            }}
            disabled={!target || !!data?.channel}
          />
        );
      },
      align: 'center',
      className: 'red',
    },
    {
      title: (
        <div style={{ position: 'relative' }}>
          <div>
            {`환불 금액`}
            <span style={{ color: 'red' }}>**</span>
          </div>
        </div>
      ),
      width: 180,
      render: (item) => {
        const refundItemIndex = getRefundItemIndex(item);
        let target = undefined as any;
        if (data?.channel) {
          target = getMatchedItem(data?.remainPurchases, item);
        } else {
          target = getMatchedItem(refundTargets, item);
        }

        return (
          <InputNumber
            value={target?.fee || 0}
            min={0}
            max={(originRefundTargets[refundItemIndex]?.fee || 0) * (originRefundTargets[refundItemIndex]?.count || 0)}
            onChange={(value) => {
              setRefundTargets((prev) => {
                const newValue = [...prev];
                newValue[refundItemIndex].fee = value || 0;
                return newValue;
              });
            }}
            disabled={!target || !!data?.channel}
            addonAfter={
              !!target && (
                <Select
                  disabled={!!data?.channel}
                  defaultValue={100}
                  popupMatchSelectWidth={false}
                  suffixIcon={null}
                  options={Array.from({ length: 20 }, (_, idx) => ({
                    value: 5 * (idx + 1),
                    label: `${5 * (idx + 1)}%`,
                  }))}
                  onChange={(e) => {
                    setRefundTargets((prev) => {
                      const newValue = [...prev];
                      const refundTargetIndex = getRefundItemIndex(item);
                      newValue[refundTargetIndex].fee = Math.floor(
                        (originRefundTargets[refundTargetIndex]?.fee || 0) *
                          (originRefundTargets[refundTargetIndex]?.count || 0) *
                          (e / 100),
                      );
                      return newValue;
                    });
                  }}
                />
              )
            }
          />
        );
      },
      align: 'center',
      hidden: !!data?.channel,
    },
  ];

  const ALLOW_REFUND_INFO: DescriptionsItemProps[] = [
    {
      span: 3,
      label: '',
      children: (
        <div>
          <Table
            style={{ whiteSpace: 'pre-wrap' }}
            columns={data?.channel ? REQUEST_REFFUND_TABLE_COLUMNS : ALLOW_TABLE_COLUMNS}
            dataSource={
              data?.channel
                ? data?.remainPurchases || data?.purchases
                : (data?.remainPurchases || data?.purchases)?.filter((item) => item.fee)
            }
            pagination={false}
          />
          <div className="error">
            {`* 일정을 취소하는 경우에만 일정 취소 수량을 입력해주세요. 일정취소 없는 부분환불은 0 입력.\n** 부분환불 시 환불할 전체 금액을 입력해주세요.`}
          </div>
        </div>
      ),
      labelStyle: { display: 'none' },
    },
    {
      span: 3,
      label: '총 환불 금액',
      children: data?.channel ? `${data?.remainAmount?.toLocaleString()}원` : `${refundAmount.toLocaleString()}원`,
    },
  ];
  const REQUEST_REFUND_INFO: DescriptionsItemProps[] = [
    {
      span: 3,
      label: '',
      children: (
        <div>
          <Table
            style={{ whiteSpace: 'pre-wrap' }}
            title={() => {
              let content = '';
              if (data?.channel) {
                content =
                  '* 네이버/카카오 결제건의 경우 전체환불 요청만 가능합니다. \n* 부분환불을 원하실 경우 카카오 파트너스로 문의주시기 바랍니다. ';
              }
              return <div style={{ color: 'red' }}>{content}</div>;
            }}
            columns={REQUEST_REFFUND_TABLE_COLUMNS}
            dataSource={data?.remainPurchases || []}
            pagination={false}
          />
          <div className="error">
            {`* 일정을 취소하는 경우에만 일정 취소 수량을 입력해주세요. 일정취소 없는 부분환불은 0 입력.\n** 부분환불 시 환불할 전체 금액을 입력해주세요.`}
          </div>
        </div>
      ),
      labelStyle: { display: 'none' },
    },
    {
      span: 3,
      label: '총 환불 금액',
      children: data?.channel
        ? `${(data?.remainAmount || 0).toLocaleString()}원`
        : `${refundAmount.toLocaleString()}원`,
    },
  ];
  const refundReasonFields = [
    {
      label: '환불사유',
      content: (
        <Select
          className="sale-modal__refund-reason-select"
          placeholder="환불사유를 선택해 주세요"
          value={selectedRefundReasonKey || undefined}
          onChange={setSelectedRefundReasonKey}
          options={REFUND_REASON_OPTIONS.map((option) => ({ value: option.key, label: option.label }))}
          style={{ width: '100%' }}
          size="large"
        />
      ),
    },
    ...(selectedRefundReasonKey === 'etc'
      ? [
          {
            label: '고객 안내 사유',
            content: (
              <Input
                className="sale-modal__customer-reason"
                value={customerReasonText}
                onChange={(event) => setCustomerReasonText(event.target.value)}
                placeholder="고객 알림톡에 전달될 사유를 입력해 주세요."
                maxLength={80}
              />
            ),
          },
        ]
      : []),
    {
      label: '상세 사유 및 요청사항',
      content: (
        <textarea
          className="sale-modal__memo"
          style={{ borderRadius: '8px', padding: '15px 16px' }}
          ref={memoRef}
          placeholder="(선택) 상세 사유 및 환불 관련하여 관리자에게 전달할 내용을 입력해 주세요."
        />
      ),
    },
  ];
  const modalTitle: { [key: string]: string } = {
    INFO: '환불처리',
    ACCEPTED: '환불예정',
    DENIED: '환불반려',
    PARTNER_REQUEST: '환불요청',
  };
  const modalContent = {
    INFO: [
      {
        label: '',
        content: <Descriptions title="환불정보" items={REFUND_INFO} bordered labelStyle={{ width: 150 }} />,
      },
    ],
    ACCEPTED: [
      {
        label: '',
        content: <Descriptions title="환불금액" items={ALLOW_REFUND_INFO} bordered />,
      },
      ...refundReasonFields,
      {
        label: data?.channel ? '' : '추가 환불불가',
        content: data?.channel ? undefined : (
          <NotRefundSwitch
            checked={noMoreRefundsAvailable}
            onChange={() => setNoMoreRefundsAvailable((prev) => !prev)}
          />
        ),
      },
    ],
    DENIED: [
      {
        label: '환불 반려 사유',
        content: (
          <textarea
            className="sale-modal__memo"
            ref={memoRef}
            placeholder="판매자 환불 시 구매자에게 정확한 사유를 안내해 주세요."
          />
        ),
      },
      {
        label: data?.channel ? '' : '추가 환불불가',
        content: data?.channel ? undefined : (
          <NotRefundSwitch
            checked={noMoreRefundsAvailable}
            onChange={() => setNoMoreRefundsAvailable((prev) => !prev)}
          />
        ),
      },
    ],
    PARTNER_REQUEST: [
      {
        label: '',
        content: <Descriptions title="환불금액" items={REQUEST_REFUND_INFO} bordered />,
      },
      ...refundReasonFields,
      {
        label: data?.channel ? '' : '추가 환불불가',
        content: data?.channel ? undefined : (
          <NotRefundSwitch
            checked={noMoreRefundsAvailable}
            onChange={() => setNoMoreRefundsAvailable((prev) => !prev)}
          />
        ),
      },
    ],
  };
  const modalFooter = {
    INFO: (
      <div className="sale-modal__footer">
        <button className="sale-modal__btn sale-modal__btn--black" onClick={() => changeStep('ACCEPTED')}>
          승인
        </button>
        <button className="sale-modal__btn sale-modal__btn--black" onClick={() => changeStep('DENIED')}>
          반려
        </button>
        <button className="sale-modal__btn sale-modal__btn--white" onClick={() => close()}>
          닫기
        </button>
      </div>
    ),
    ACCEPTED: (
      <div className="sale-modal__footer">
        <button className="sale-modal__btn sale-modal__btn--black" onClick={handleAcceptRefund}>
          승인
        </button>
        <button className="sale-modal__btn sale-modal__btn--white" onClick={() => close()}>
          닫기
        </button>
      </div>
    ),
    DENIED: (
      <div className="sale-modal__footer">
        <button className="sale-modal__btn sale-modal__btn--black" onClick={handleDenyRefund}>
          반려
        </button>
        <button className="sale-modal__btn sale-modal__btn--white" onClick={() => close()}>
          닫기
        </button>
      </div>
    ),
    PARTNER_REQUEST: (
      <div className="sale-modal__footer">
        <button className="sale-modal__btn sale-modal__btn--black" onClick={handleRequestRefund}>
          환불요청
        </button>
        <button className="sale-modal__btn sale-modal__btn--white" onClick={() => close()}>
          닫기
        </button>
      </div>
    ),
  };

  if (!show) {
    return <></>;
  }

  return (
    <Modal
      className="sale-modal"
      centered
      open={show}
      width={['ACCEPTED', 'PARTNER_REQUEST'].includes(step) ? 1000 : undefined}
      zIndex={998}
      title={modalTitle[step]}
      footer={modalFooter[step]}
      onCancel={close}>
      {modalContent[step].map((item) => (
        <div key={item.label} className="sale-modal__content">
          {item.label && (
            <div
              className={`sale-modal__label${
                item.label === '환불사유' || item.label === '고객 안내 사유' ? ' required' : ''
              }`}>
              {item.label}
            </div>
          )}
          {item.content}
        </div>
      ))}
    </Modal>
  );
};

export default RefundProductOrderModal;
